import GameMode from './GameMode.js';
import {
  ASTEROID_INITIAL_COUNT,
  CTF_BASE_SIZE,
  CTF_CAPTURE_BONUS,
  CTF_FLAG_RADIUS,
  CTF_FLAG_SPAWN_HEIGHT,
  CTF_FLAG_SPAWN_WIDTH,
  CTF_FLAG_PICKUP_BONUS,
  CTF_KILL_BONUS,
  CTF_ROUND_DURATION_MS,
  CTF_RESPAWN_OFFSET,
  SHIELD_DEFAULT_COUNT,
  SHIP_RADIUS,
  CTF_TEAMS
} from 'asteroids-shared/constants';
import { wrappedDelta } from 'asteroids-shared/helpers';

const TEAM_BY_ID = new Map(CTF_TEAMS.map(team => [team.id, team]));

function buildBases(worldWidth, worldHeight) {
  const cy = worldHeight / 2;
  return [
    {
      ...TEAM_BY_ID.get('yellow'),
      x: worldWidth / 4,
      y: cy,
      size: CTF_BASE_SIZE
    },
    {
      ...TEAM_BY_ID.get('cyan'),
      x: (worldWidth * 3) / 4,
      y: cy,
      size: CTF_BASE_SIZE
    }
  ];
}

function overlapsCircle(ship, targetX, targetY, targetRadius, worldBounds) {
  const dx = worldBounds?.toroidalMap === false
    ? ship.position.x - targetX
    : wrappedDelta(ship.position.x, targetX, worldBounds.width);
  const dy = worldBounds?.toroidalMap === false
    ? ship.position.y - targetY
    : wrappedDelta(ship.position.y, targetY, worldBounds.height);
  const r = ship.radius + targetRadius;
  return dx * dx + dy * dy <= r * r;
}

function overlapsRect(ship, rect, worldBounds) {
  const dx = worldBounds?.toroidalMap === false
    ? ship.position.x - rect.x
    : wrappedDelta(ship.position.x, rect.x, worldBounds.width);
  const dy = worldBounds?.toroidalMap === false
    ? ship.position.y - rect.y
    : wrappedDelta(ship.position.y, rect.y, worldBounds.height);
  const hx = (rect.size ?? CTF_BASE_SIZE) / 2;
  const hy = (rect.size ?? CTF_BASE_SIZE) / 2;
  const cx = Math.max(-hx, Math.min(dx, hx));
  const cy = Math.max(-hy, Math.min(dy, hy));
  const ox = dx - cx;
  const oy = dy - cy;
  return ox * ox + oy * oy <= ship.radius * ship.radius;
}

function randomFlagSpawn(flagHome, world) {
  return {
    x: flagHome.x + world.randomBetween(-CTF_FLAG_SPAWN_WIDTH / 2, CTF_FLAG_SPAWN_WIDTH / 2),
    y: flagHome.y + world.randomBetween(-CTF_FLAG_SPAWN_HEIGHT / 2, CTF_FLAG_SPAWN_HEIGHT / 2)
  };
}

function randomRespawnNearBase(base, rotation, world) {
  const half = (base.size ?? CTF_BASE_SIZE) / 2;
  let dx = world.randomBetween(-CTF_RESPAWN_OFFSET, CTF_RESPAWN_OFFSET);
  let dy = world.randomBetween(-CTF_RESPAWN_OFFSET, CTF_RESPAWN_OFFSET);
  const paddedHalf = half + SHIP_RADIUS + 2;

  if (Math.abs(dx) < paddedHalf && Math.abs(dy) < paddedHalf) {
    if (Math.abs(dx) >= Math.abs(dy)) {
      dx = dx < 0 ? -paddedHalf : paddedHalf;
    } else {
      dy = dy < 0 ? -paddedHalf : paddedHalf;
    }
  }

  return {
    x: base.x + dx,
    y: base.y + dy,
    rotation
  };
}

export default class CTFMode extends GameMode {
  static get name() { return 'ctf'; }

  constructor({
    roundDuration = CTF_ROUND_DURATION_MS,
    killBonus = CTF_KILL_BONUS,
    captureBonus = CTF_CAPTURE_BONUS,
    pickupBonus = CTF_FLAG_PICKUP_BONUS,
    asteroidsEnabled = false,
    shieldsEnabled = false
  } = {}) {
    super();
    this.roundDuration = roundDuration;
    this.killBonus = killBonus;
    this.captureBonus = captureBonus;
    this.pickupBonus = pickupBonus;
    this.startedAt = 0;
    this.scores = new Map();
    this.teamTotals = { yellow: 0, cyan: 0 };
    this.worldBounds = null;
    this.bases = [];
    this.flagHome = { x: 0, y: 0 };
    this.flag = { x: 0, y: 0, carrierId: null };
    this.asteroidsEnabled = asteroidsEnabled === true;
    this.shieldsEnabled = shieldsEnabled === true;
    this.asteroidCount = ASTEROID_INITIAL_COUNT;
  }

  get shipVsShipCollision() { return true; }
  get bulletHitsFriendly() { return false; }
  get sharedScore() { return false; }
  get initialLives() { return Infinity; }
  get respawnDelay() { return 2000; }
  get timedGameMs() { return this.roundDuration; }
  get spawnInvulnerabilityDuration() { return 2500; }
  get disableFireDuringSpawnInvulnerability() { return true; }
  get teams() { return CTF_TEAMS; }

  getSpawnPositions({ worldWidth, worldHeight, players = [], playerCount = players.length }) {
    const bases = buildBases(worldWidth, worldHeight);
    const roster = players.length ? players : Array.from({ length: playerCount }, (_, index) => ({ id: index }));
    const counts = new Map();
    for (const player of roster) {
      const key = player.teamId ?? 'yellow';
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }

    const used = new Map();
    return roster.map(player => {
      const teamId = player.teamId ?? 'yellow';
      const base = bases.find(entry => entry.id === teamId) ?? bases[0];
      const count = counts.get(teamId) ?? 1;
      const slot = used.get(teamId) ?? 0;
      used.set(teamId, slot + 1);
      const spread = 110;
      const y = base.y + (slot - (count - 1) / 2) * spread;
      return {
        x: base.x,
        y,
        rotation: teamId === 'yellow' ? 90 : 270
      };
    });
  }

  onGameStart(world, players, ctx) {
    this.startedAt = ctx.now();
    this.scores.clear();
    this.teamTotals = { yellow: 0, cyan: 0 };
    this.worldBounds = world.worldBounds;
    this.bases = buildBases(world.worldBounds.width, world.worldBounds.height);
    this.flagHome = {
      x: world.worldBounds.width / 2,
      y: world.worldBounds.height / 2
    };
    this.resetFlag(world);
    if (this.asteroidsEnabled) world.spawnAsteroidWave(this.asteroidCount);
    if (this.shieldsEnabled) {
      for (const ship of world.ships.values()) {
        ship.shieldMax = SHIELD_DEFAULT_COUNT;
        ship.shieldHealth = SHIELD_DEFAULT_COUNT;
      }
    }

    for (const player of players) {
      this.scores.set(player.id, {
        playerId: player.id,
        name: player.name,
        teamId: player.teamId ?? null,
        teamName: player.teamName ?? null,
        score: 0,
        kills: 0,
        deaths: 0,
        captures: 0
      });
    }

    this.broadcastScore(ctx);
  }

  onTick(world, _tick, ctx) {
    if (this.flag.carrierId != null) {
      const carrier = world.ships.get(this.flag.carrierId);
      if (!carrier || !carrier.alive) return;
      this.flag.x = carrier.position.x;
      this.flag.y = carrier.position.y;
      const touchedBase = this.bases.find(base => overlapsRect(carrier, base, this.worldBounds)) ?? null;
      if (touchedBase) {
        const stats = this.statsFor(carrier.id);
        let scoringTeamId = touchedBase.id;
        let ownGoal = false;
        if (touchedBase.id === carrier.teamId) {
          stats.score += this.captureBonus;
          stats.captures += 1;
        } else {
          ownGoal = true;
        }
        this.awardTeamScore(scoringTeamId, this.captureBonus);
        this.resetFlag(world);
        ctx.broadcastHudFeed({
          method: 'flag_capture',
          playerId: carrier.id,
          teamId: carrier.teamId,
          scoringTeamId,
          ownGoal,
          scoreDelta: this.captureBonus
        });
        this.broadcastScore(ctx);
      }
      return;
    }

    const ships = [...world.ships.values()]
      .filter(ship => ship.alive && ship.teamId != null)
      .sort((a, b) => String(a.id).localeCompare(String(b.id)));

    for (const ship of ships) {
      if (!overlapsCircle(ship, this.flag.x, this.flag.y, CTF_FLAG_RADIUS, this.worldBounds)) continue;
      this.flag.carrierId = ship.id;
      this.flag.x = ship.position.x;
      this.flag.y = ship.position.y;
      this.statsFor(ship.id).score += this.pickupBonus;
      ctx.broadcastHudFeed({
        method: 'flag_pickup',
        playerId: ship.id,
        teamId: ship.teamId
      });
      this.broadcastScore(ctx);
      break;
    }
  }

  onShipHit(_world, ship, hitBy, ctx) {
    const victim = this.statsFor(ship.id);
    victim.name = victim.name ?? ship.name;
    victim.teamId = victim.teamId ?? ship.teamId ?? null;
    victim.teamName = victim.teamName ?? ship.teamName ?? null;
    victim.deaths += 1;

    if (hitBy && hitBy !== ship.id) {
      const killer = this.statsFor(hitBy);
      const killerShip = _world.ships.get(hitBy);
      if (killerShip?.name) killer.name = killer.name ?? killerShip.name;
      if (killerShip?.teamId) killer.teamId = killer.teamId ?? killerShip.teamId;
      if (killerShip?.teamName) killer.teamName = killer.teamName ?? killerShip.teamName;
      killer.kills += 1;
      killer.score += this.killBonus;
      const rp = ctx.ramPairIds;
      const skipKillLine = rp?.length === 2
        && (ship.id === rp[0] || ship.id === rp[1])
        && (hitBy === rp[0] || hitBy === rp[1]);
      if (!skipKillLine) {
        ctx.broadcastHudFeed({
          killerId: hitBy,
          victimId: ship.id,
          method: 'kill',
          scoreDelta: this.killBonus
        });
      }
    }

    if (this.flag.carrierId === ship.id) {
      this.dropFlagAt(ship.position.x, ship.position.y, ship.id, ctx);
    }

    ctx.scheduleRespawn(ship.id, this.respawnDelay);
    this.broadcastScore(ctx);
  }

  onPlayerLeave(_world, playerId, ctx, ship = null) {
    if (this.flag.carrierId === playerId) {
      if (ship?.position) this.dropFlagAt(ship.position.x, ship.position.y, playerId, ctx);
      else this.resetFlag(_world);
    }
    this.broadcastScore(ctx);
  }

  getRespawnPosition({ player, world }) {
    if (!player?.teamId || !world) return null;
    const base = this.baseForTeam(player.teamId);
    if (!base) return null;
    return randomRespawnNearBase(base, player.teamId === 'yellow' ? 90 : 270, world);
  }

  onAllAsteroidsCleared(world) {
    if (this.asteroidsEnabled) world.spawnAsteroidWave(this.asteroidCount)
  }

  onShipCollide(_world, shipA, shipB, ctx) {
    if (shipA.teamId != null && shipA.teamId === shipB.teamId) return;
    const pair = [shipA.id, shipB.id];
    pair.sort((a, b) => String(a).localeCompare(String(b)));
    ctx.ramPairIds = pair;
    try {
      ctx.hitShip(shipA.id, shipB.id);
      ctx.hitShip(shipB.id, shipA.id);
    } finally {
      ctx.broadcastHudFeed({
        method: 'ram',
        playerA: pair[0],
        playerB: pair[1]
      });
      delete ctx.ramPairIds;
    }
  }

  checkGameOver(_world, ctx) {
    const elapsed = ctx.now() - this.startedAt;
    if (elapsed < this.timedGameMs) return null;
    const standings = this.sortedStandings(ctx);
    const teamScores = this.teamScores();
    let winnerTeamId = null;
    if (teamScores.yellow > teamScores.cyan) winnerTeamId = 'yellow';
    else if (teamScores.cyan > teamScores.yellow) winnerTeamId = 'cyan';
    return {
      reason: 'time_up',
      winnerId: standings[0]?.playerId ?? null,
      winnerTeamId,
      standings,
      teamScores,
      returnLobbyCapMs: 30000
    };
  }

  getSnapshotState() {
    return {
      kind: 'ctf',
      bases: this.bases.map(base => ({ ...base })),
      flag: {
        x: this.flag.x,
        y: this.flag.y,
        homeX: this.flagHome.x,
        homeY: this.flagHome.y,
        carrierId: this.flag.carrierId
      },
      teamScores: this.teamScores()
    };
  }

  statsFor(playerId) {
    let stats = this.scores.get(playerId);
    if (!stats) {
      stats = {
        playerId,
        name: null,
        teamId: null,
        teamName: null,
        score: 0,
        kills: 0,
        deaths: 0,
        captures: 0
      };
      this.scores.set(playerId, stats);
    }
    return stats;
  }

  baseForTeam(teamId) {
    return this.bases.find(base => base.id === teamId) ?? null;
  }

  sortedStandings(ctx) {
    const now = ctx?.now?.() ?? Date.now();
    return [...this.scores.values()]
      .map(entry => ({ ...entry }))
      .sort((a, b) =>
        b.score - a.score
        || b.captures - a.captures
        || b.kills - a.kills
        || a.deaths - b.deaths
        || String(a.playerId).localeCompare(String(b.playerId))
      )
      .map((entry, index) => ({ ...entry, rank: index + 1, at: now }));
  }

  teamScores() {
    return { ...this.teamTotals };
  }

  broadcastScore(ctx) {
    ctx.broadcastScore({
      scoreboard: this.sortedStandings(ctx),
      teamScores: this.teamScores(),
      reason: null
    });
  }

  resetFlag(world) {
    const spawn = randomFlagSpawn(this.flagHome, world);
    this.flag = {
      x: spawn.x,
      y: spawn.y,
      carrierId: null
    };
  }

  dropFlagAt(x, y, playerId, ctx) {
    this.flag = { x, y, carrierId: null };
    ctx.broadcastHudFeed({
      method: 'flag_drop',
      playerId
    });
  }

  awardTeamScore(teamId, delta) {
    if (teamId !== 'yellow' && teamId !== 'cyan') return;
    this.teamTotals[teamId] += delta;
  }
}
